import { useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation, useParams } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { UserCircledIcon } from 'components/Icons/UserCircledIcon'
import Heading from 'components/v2/Heading'
import { Content, ContentHeader, ContentBody } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { TransactionsList } from 'features/Kredits/views/Transactions/List'
import { TransactionsPagination } from 'features/Kredits/views/Transactions/Pagination'
import { GTKredit } from 'gql/types.generated'
import { GET_KREDIT_TRANSACTIONS, GET_KREDIT } from 'features/Kredits/queries-legacy'
import styled from 'styled-components'
import { useLogin } from 'features/Login/hooks'
import { getQueries } from 'utils/route'
import { canRead } from 'features/Users/utils'

const TRANSACTIONS_LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'kredits.transactions.helmet',
    defaultMessage: 'Kredit: History | Nexbus Admin'
  }
})

const User = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const TransactionsView = (): ReactElement => {
  const location = useLocation()
  const intl = useIntl()
  const login = useLogin()
  const { kreditId } = useParams()

  let content
  const queries = getQueries(location.search)
  const { page = '1' } = queries
  const pageNb = parseInt(page)
  const hasPermission = canRead(FEATURES.KREDIT, login.accessToken.policies)

  const {
    loading: kreditLoading,
    error: kreditError,
    data: kreditData,
    refetch: kreditRefetch,
    networkStatus: kreditNetworkStatus
  } = useQuery<{ kredit: GTKredit }>(GET_KREDIT, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      id: kreditId
    }
  })
  const {
    loading: kreditTransactionsLoading,
    error: kreditTransactionsError,
    data: kreditTransactionsData,
    refetch: kreditTransactionsRefetch,
    networkStatus: kreditTransactionsNetworkStatus
  } = useQuery(GET_KREDIT_TRANSACTIONS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        kreditID: kreditId
      },
      pagination: {
        page: pageNb - 1,
        size: TRANSACTIONS_LIMIT
      }
    }
  })

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
    if (hasPermission) {
      kreditRefetch()
      kreditTransactionsRefetch()
    }
  }, [])

  if (!hasPermission) {
    return <ForbiddenView feature={FEATURES.KREDIT} />
  }

  const loading =
    kreditLoading ||
    kreditTransactionsLoading ||
    kreditNetworkStatus === NetworkStatus.refetch ||
    kreditTransactionsNetworkStatus === NetworkStatus.refetch
  if (loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (kreditError || kreditTransactionsError) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="kredits.transactions.error.description"
            defaultMessage="There was an error while loading the transactions"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              if (kreditError) {
                kreditRefetch()
              }
              if (kreditTransactionsError) {
                kreditTransactionsRefetch()
              }
            }}
            kind="primary"
          >
            <FormattedMessage id="kredits.transactions.error.action" defaultMessage="Reload the transactions list" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(kreditTransactionsData?.kreditTransactions?.items) && pageNb === 1) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="kredits.transactions.empty.description"
            defaultMessage="There are no transactions for the moment"
          />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else if (isEmpty(kreditTransactionsData?.kreditTransactions?.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="kredits.transactions.empty.filters.description"
            defaultMessage="There were no transactions found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to={`/kredits/${kreditId}/transactions`} kind="primary">
            <FormattedMessage id="kredits.transactions.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <User>
          <UserCircledIcon />
          <Heading.H4>{kreditData?.kredit?.user?.userFullName}</Heading.H4>
        </User>
        <TransactionsList transactions={kreditTransactionsData?.kreditTransactions?.items ?? []} />
        <TransactionsPagination
          page={pageNb}
          totalItems={kreditTransactionsData?.kreditTransactions?.total ?? 0}
          limit={TRANSACTIONS_LIMIT}
          currentItemsCount={kreditTransactionsData?.kreditTransactions?.items.length ?? 0}
          kreditId={kreditId ?? ''}
        />
      </>
    )
  }

  return (
    <Content>
      <ContentHeader isResponsive>
        <Heading.H3>
          <FormattedMessage id="kredits.transactions.header.label" defaultMessage="Kredit: History" />
        </Heading.H3>
      </ContentHeader>
      <ContentBody>
        <section>{content}</section>
      </ContentBody>
    </Content>
  )
}

export default withLastUrl(TransactionsView)
